/**
 * Relay event helpers.
 *
 * Race events vs. benchmark events:
 *
 *   - `races.event` holds the relay's overall type (e.g. '4x800m-relay',
 *     'smr-1600', 'dmr'). This names the race the athlete ran in.
 *   - `races.relay_leg` holds the leg distance in meters (string) the
 *     athlete actually ran. For same-distance relays (4×N) this is
 *     implied; for mixed relays (SMR / DMR) the athlete picks.
 *   - `benchmarks.event` for a relay result uses the `*-split`
 *     pseudo-events ('400m-split', '800m-split', …). Distinct
 *     namespace from open-track events so Top 10 / improvement
 *     tracking never mashes an open 800m with an 800m relay leg.
 *
 * Everything a UI needs to handle a relay row goes through these
 * helpers — race-form dropdowns, benchmark writes, and display
 * formatting all consume the same source of truth.
 */

// Race-event key → ordered list of leg distances (meters). Same-distance
// relays have one-entry lists (auto-selected); mixed relays have
// multiple entries so the athlete can pick which leg they ran.
const RELAY_LEGS = {
  '4x100m-relay': [100],
  '4x200m-relay': [200],
  '4x400m-relay': [400],
  '4x800m-relay': [800],
  '4x1600m-relay': [1600],
  'smr-800': [100, 200, 400],
  'smr-1600': [200, 400, 800],
  'dmr': [1200, 400, 800, 1600],
};

// Meters → split benchmark event key. Only the leg distances we
// actually support across the supported relays.
const SPLIT_EVENT_BY_METERS = {
  100: '100m-split',
  200: '200m-split',
  400: '400m-split',
  800: '800m-split',
  1200: '1200m-split',
  1600: '1600m-split',
};

/** True when `event` is a relay race event (key lives in RELAY_LEGS). */
export function isRelayEvent(event) {
  return !!event && Object.prototype.hasOwnProperty.call(RELAY_LEGS, event);
}

/**
 * Leg-distance options (in meters) for a relay race event, in the order
 * the athlete typically thinks of them (SMR/DMR show the first leg
 * first). Returns null for non-relays; returns `[N]` for same-distance
 * relays where the leg is auto-selected.
 */
export function getRelayLegOptions(event) {
  if (!isRelayEvent(event)) return null;
  return [...RELAY_LEGS[event]];
}

/**
 * True when the relay has exactly one leg distance (4×N). The UI
 * should auto-select and hide the leg picker in this case.
 */
export function hasFixedRelayLeg(event) {
  const opts = getRelayLegOptions(event);
  return !!opts && opts.length === 1;
}

/**
 * Default leg distance for a relay event. For same-distance relays
 * returns that distance; for mixed relays returns the first option
 * (first leg). Returns null for non-relays.
 */
export function defaultRelayLeg(event) {
  const opts = getRelayLegOptions(event);
  return opts ? opts[0] : null;
}

/**
 * Given a relay race event and the athlete's actual leg (string or
 * number, meters), return the split-event key to store in
 * `benchmarks.event`. Returns null when the input isn't a valid
 * relay/leg combo.
 */
export function relaySplitEvent(raceEvent, leg) {
  if (!isRelayEvent(raceEvent)) return null;
  const meters = parseInt(leg, 10);
  if (!meters) return null;
  const options = getRelayLegOptions(raceEvent);
  if (!options.includes(meters)) return null;
  return SPLIT_EVENT_BY_METERS[meters] ?? null;
}

/**
 * Back-compat helper: given a benchmark's event, return true when it
 * is a relay-split pseudo-event. Useful for improvement tracking UIs
 * that want to render a "(split)" annotation.
 */
export function isSplitEvent(event) {
  return typeof event === 'string' && event.endsWith('-split');
}

/**
 * Label to show next to the result-entry input. For relay events we
 * show the split label ("800m (split)") instead of the relay wrapper
 * ("1600 SMR (200-200-400-800)") — the benchmark row gets stored
 * under the split key anyway, so this keeps the input and the saved
 * event in sync for the athlete.
 *
 * `eventsMap` is passed in by the caller (vdot.js EVENTS) to avoid
 * pulling a heavyweight module dependency into this helpers file.
 */
export function resultInputLabel(raceEvent, relayLeg, eventsMap) {
  const key = isRelayEvent(raceEvent)
    ? (relaySplitEvent(raceEvent, relayLeg) ?? raceEvent)
    : raceEvent;
  return eventsMap?.[key]?.label ?? raceEvent;
}

/**
 * Pull the athlete's leg distance for a specific relay event in a
 * race. Reads the per-event `relay_legs` map first; falls back to
 * the legacy `relay_leg` text column when the map isn't populated
 * (so old rows that ran a single relay still resolve correctly).
 *
 * Returns null when the race is null or the event isn't tracked.
 */
export function getRelayLegFor(race, raceEventKey) {
  if (!race || !raceEventKey) return null;
  const map = race.relay_legs;
  if (map && Object.prototype.hasOwnProperty.call(map, raceEventKey)) {
    return map[raceEventKey] ?? null;
  }
  // Legacy: single relay per race stored in `relay_leg`. Only use
  // it if the requested event is itself a relay — guards against
  // accidentally returning a leg for an open-track event.
  if (isRelayEvent(raceEventKey)) {
    return race.relay_leg ?? null;
  }
  return null;
}

/**
 * Resolve which calendar date a specific event in this race
 * happened on. For multi-day meets, the coach/athlete picks a date
 * per event in `race.event_dates`. For single-day races (or events
 * without a per-event override) we fall back to `race.race_date`.
 *
 * Always returns a YYYY-MM-DD string when `race.race_date` is set,
 * or null otherwise.
 */
export function getEventDate(race, eventKey) {
  if (!race) return null;
  const map = race.event_dates;
  if (map && eventKey && map[eventKey]) return map[eventKey];
  return race.race_date ?? null;
}

/**
 * Resolve the benchmark event key for a race event. Relay events
 * map to their split pseudo-event ('800m-split'); open-track events
 * pass through unchanged. Centralizes the logic that was duplicated
 * across every benchmark write + prefill lookup.
 */
export function benchmarkEventFor(race, raceEventKey) {
  if (!isRelayEvent(raceEventKey)) return raceEventKey;
  return relaySplitEvent(raceEventKey, getRelayLegFor(race, raceEventKey)) ?? raceEventKey;
}
